import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import CategoryGrid from '@/components/CategoryGrid';
import ProductShowcase from '@/components/ProductShowcase';
import ArticleSection from '@/components/ArticleSection';
import { productCategories } from '@/mocks/products';

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const reviewLinks = [
    { label: t('review.shipping'), url: 'https://kaspreadsheet.com' },
    { label: t('review.warehouse'), url: 'https://kaspreadsheet.com' },
    { label: t('review.legit'), url: 'https://kaspreadsheet.com' },
    { label: t('review.coupon'), url: 'https://kaspreadsheet.com' },
    { label: t('review.discount'), url: 'https://kaspreadsheet.com' },
  ];

  const steps = [
    t('howitworks.step1'),
    t('howitworks.step2'),
    t('howitworks.step3'),
    t('howitworks.step4'),
    t('howitworks.step5'),
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[560px] md:min-h-[640px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=a%20vibrant%20editorial%20flat%20lay%20of%20colorful%20sneakers%20hoodies%20and%20accessories%20artfully%20arranged%20on%20a%20warm%20terracotta%20backdrop%20with%20bold%20soft%20shadows%20and%20modern%20streetwear%20styling&width=1600&height=900&seq=hero-main-01&orientation=landscape&nocache=true"
            alt="AllChinaBuy Spreadsheet"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 max-w-4xl mx-auto w-full">
          <p className="text-emerald-400 text-sm md:text-base font-semibold tracking-wide uppercase mb-3">AllChinaBuy Spreadsheet 2026</p>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
            {t('hero.subtitle')}
          </h1>
          <p className="text-white/80 text-base md:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            {t('hero.description')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-6">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 transition-colors whitespace-nowrap"
            >
              {t('hero.cta.primary')}
              <i className="ri-external-link-line"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white text-gray-900 font-semibold text-sm hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              {t('hero.cta.secondary')}
              <i className="ri-shopping-cart-line"></i>
            </a>
          </div>
          <p className="text-white/60 text-xs md:text-sm">{t('trust.line')}</p>
        </div>
      </section>

      {/* Spreadsheet Intro */}
      <section className="py-16 md:py-20 bg-white">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{t('spreadsheet.title')}</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                {t('spreadsheet.description')}
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-50 text-emerald-700 font-semibold text-sm hover:bg-emerald-100 transition-colors"
              >
                {t('spreadsheet.cta')}
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
            <div className="rounded-2xl overflow-hidden bg-gray-100 shadow-sm">
              <img
                src="https://readdy.ai/api/search-image?query=a%20sleek%20laptop%20on%20a%20light%20oak%20desk%20showing%20a%20colorful%20grid%20of%20product%20thumbnails%20beside%20a%20small%20green%20plant%20and%20a%20notebook%20in%20a%20bright%20airy%20workspace&width=800&height=600&seq=spreadsheet-preview-01&orientation=landscape&nocache=true"
                alt="AllChinaBuy Spreadsheet Preview"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      <CategoryGrid />

      <ProductShowcase categories={productCategories} />

      {/* How It Works */}
      <section className="py-16 md:py-20 bg-white">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">{t('howitworks.title')}</h2>
            <p className="text-gray-600 text-sm md:text-base max-w-xl mx-auto">Follow these simple steps to get the most from your AllChinaBuy spreadsheet research</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
            {steps.map((step, i) => (
              <div key={i} className="relative flex flex-col items-center text-center p-5 rounded-xl bg-gray-50 border border-gray-100">
                <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center text-sm font-bold mb-3">
                  {i + 1}
                </div>
                <p className="text-sm font-medium text-gray-700">{step}</p>
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 -right-3 w-6 h-px bg-emerald-200"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 md:py-20 bg-emerald-50">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">{t('trust.title')}</h2>
            <p className="text-gray-600 text-sm md:text-base max-w-xl mx-auto">{t('trust.description')}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { icon: 'ri-shield-check-line', title: 'Verified Info', desc: 'Cross-checked resources' },
              { icon: 'ri-search-line', title: 'Product Research', desc: 'Detailed comparisons' },
              { icon: 'ri-truck-line', title: 'Shipping Data', desc: 'Cost & time analysis' },
              { icon: 'ri-percent-line', title: 'Discounts', desc: 'Latest coupons & codes' },
            ].map((item) => (
              <div key={item.title} className="text-center p-5 rounded-xl bg-white border border-emerald-100">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                  <i className={`${item.icon} text-emerald-600 text-lg`}></i>
                </div>
                <h3 className="font-semibold text-gray-900 text-sm mb-1">{item.title}</h3>
                <p className="text-gray-500 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Hub */}
      <section id="reviews" className="py-16 md:py-20 bg-white">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">{t('review.title')}</h2>
            <p className="text-gray-600 text-sm max-w-lg mx-auto">Explore detailed research resources for smarter AllChinaBuy decisions</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {reviewLinks.map((link) => (
              <a
                key={link.label}
                href={link.url}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gray-100 text-gray-700 text-sm font-medium hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
              >
                {link.label}
                <i className="ri-arrow-right-up-line text-xs"></i>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Section */}
      <section id="safety" className="py-16 md:py-20 bg-gray-900 text-white">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">{t('safety.title')}</h2>
              <p className="text-gray-400 leading-relaxed mb-6">
                {t('safety.description')}
              </p>
              <ul className="space-y-3">
                {['Verify seller reputation before ordering', 'Always check QC photos carefully', 'Use secure payment methods', 'Research shipping costs upfront', 'Keep records of all transactions'].map((tip) => (
                  <li key={tip} className="flex items-start gap-2 text-sm text-gray-300">
                    <i className="ri-check-line text-emerald-400 mt-0.5"></i>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=a%20clean%20flat%20lay%20of%20a%20secure%20online%20shopping%20concept%20with%20a%20soft%20shield%20badge%20a%20credit%20card%20and%20a%20checkmark%20on%20a%20gentle%20mint%20background%20modern%20minimal%20illustration&width=800&height=600&seq=safety-section-01&orientation=landscape&nocache=true"
                alt="Shopping Safety"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      <ArticleSection />

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-emerald-600">
        <div className="w-full px-4 sm:px-6 lg:px-10 text-center max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">{t('final.title')}</h2>
          <p className="text-emerald-100 text-sm md:text-base mb-8 leading-relaxed">{t('final.description')}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white text-emerald-700 font-semibold text-sm hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              {t('final.cta')}
              <i className="ri-external-link-line"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-emerald-700 text-white font-semibold text-sm hover:bg-emerald-800 transition-colors whitespace-nowrap"
            >
              {t('hero.cta.secondary')}
              <i className="ri-shopping-cart-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-100 py-10 md:py-12">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-md bg-emerald-600 flex items-center justify-center text-white text-xs font-bold">AC</div>
              <span className="font-semibold text-gray-900 text-sm">AllChinaBuy Spreadsheet</span>
            </div>
            <p className="text-gray-500 text-xs leading-relaxed mb-4 max-w-2xl">
              {t('footer.disclaimer')}
            </p>
            <div className="flex flex-wrap gap-4 text-xs text-gray-400">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="hover:text-gray-600 transition-colors">Main Website</a>
              <span className="text-gray-300">|</span>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="hover:text-gray-600 transition-colors">Categories</a>
              <span className="text-gray-300">|</span>
              <span className="hover:text-gray-600 transition-colors">Privacy Policy</span>
              <span className="text-gray-300">|</span>
              <span className="hover:text-gray-600 transition-colors">Terms</span>
            </div>
            <p className="text-gray-400 text-xs mt-4">© 2026 allchinabuyspread.beer. {t('footer.rights')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}